import { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { getTaskById, updateTask } from "../Api/TaskMaster";
import { getTeamById } from "../Api/TeamMaster";
import { getUsersByIds } from "../Api/Auth";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { fetchStagesByProject } from "../Api/StageMaster";
import {
  getBuildingBlocksByOrgAndProject,
  mapTaskToBuildingBlock,
  getBuildingBlocksByTask,
  removeSpecificTaskMapping,
} from "../Api/BuildingBlockMaster";

const TaskEditModal = ({ show, onHide, taskId, onTaskUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberDetails, setTeamMemberDetails] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const [buildingBlocks, setBuildingBlocks] = useState([]);
  // const [selectedBlockId, setSelectedBlockId] = useState("");
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [existingBlockIds, setExistingBlockIds] = useState([]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const username = userDetails?.username;

  // Fetch user details for team members
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setTeamMemberDetails([]);
      return;
    }
    try {
      const numericUserIds = userIds
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      if (numericUserIds.length === 0) {
        setTeamMemberDetails([]);
        return;
      }
      const response = await getUsersByIds(numericUserIds);
      if (response && response.users) {
        setTeamMemberDetails(response.users);
      } else {
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setTeamMemberDetails([]);
    }
  };

  // Fetch team members when project is available
  const fetchTeamMembers = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingTeam(true);
      // First get project details to get the team ID
      const projectData = await fetchProjectDetails(projectId);
      console.log("Fetched project data:", projectData);
      if (projectData && projectData.teamAssigned) {
        const teamData = await getTeamById(projectData.teamAssigned);
        if (teamData && teamData.teamMembers) {
          setTeamMembers(teamData.teamMembers);
          await fetchUserDetails(teamData.teamMembers);
        } else {
          setTeamMembers([]);
          setTeamMemberDetails([]);
        }
      } else {
        setTeamMembers([]);
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Get user display name similar to TaskMaster
  const getUserDisplayName = (memberId) => {
    const userDetail = teamMemberDetails.find(
      (user) => user.userId === parseInt(memberId),
    );
    if (userDetail) {
      return `${userDetail.username} (${userDetail.email})`;
    }
    return memberId; // Fallback to ID if user details not found
  };

  const fetchProjectStages = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingStages(true);
      const stageData = await fetchStagesByProject(projectId);
      console.log("Fetched stages for project:", stageData);
      if (Array.isArray(stageData)) {
        setStages(stageData);
      } else {
        setStages([]);
      }
    } catch (error) {
      console.error("Error fetching stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  // Fetch task details when modal opens
  useEffect(() => {
    if (show && taskId) {
      loadTaskDetails();
    }
  }, [show, taskId]);

  const loadTaskDetails = async () => {
    setLoading(true);
    try {
      const taskDetails = await getTaskById(taskId);
      setTaskData(taskDetails);

      // Fetch team members and stages for the project
      if (taskDetails.projectBelong) {
        await fetchTeamMembers(taskDetails.projectBelong);
        await fetchProjectStages(taskDetails.projectBelong);
      }

      // Load building blocks for project
      try {
        setLoadingBlocks(true);

        const blocks = await getBuildingBlocksByOrgAndProject(
          userDetails.organisationId,
          taskDetails.projectBelong,
        );

        setBuildingBlocks(blocks || []);

        // Get currently mapped block
        const mappedBlocks = await getBuildingBlocksByTask(taskId);

        setExistingBlockIds(mappedBlocks || []);
        setSelectedBlockIds(mappedBlocks || []);
      } catch (err) {
        console.error("Failed to load building blocks", err);
      } finally {
        setLoadingBlocks(false);
      }
    } catch (error) {
      console.error("Error loading task details:", error);
      toast.error("Failed to load task details");
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setTaskData({
      ...taskData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (e) => {
    setTaskData({
      ...taskData,
      [e.target.name]: e.target.value ? new Date(e.target.value) : null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskData) return;

    setSaving(true);
    try {
      const updateData = {
        TaskName: taskData.taskName,
        ProjectBelong: taskData.projectBelong,
        AssignedTo: taskData.assignedTo,
        TaskStartDate: taskData.taskStartDate,
        TaskEndDate: taskData.taskEndDate,
        TaskType: taskData.taskType,
        TaskPriority: taskData.taskPriority,
        TaskStatus: taskData.taskStatus,
        TaskDescription: taskData.taskDescription,
        ActualStartDate: taskData.actualStartDate,
        ActualEndDate: taskData.actualEndDate,
        EditedBy: username,
        StageBelong: taskData.stageBelong || null, // Add StageBelong to update data
      };

      await updateTask(taskId, updateData);

      // 🔥 Handle Building Block Mapping
      // 🔥 Handle Building Block Mapping (Many-to-Many)
      try {
        // 🔹 Blocks to ADD
        const blocksToAdd = selectedBlockIds.filter(
          (id) => !existingBlockIds.includes(id),
        );

        // 🔹 Blocks to REMOVE
        const blocksToRemove = existingBlockIds.filter(
          (id) => !selectedBlockIds.includes(id),
        );

        // 🔹 Add new mappings
        for (const blockId of blocksToAdd) {
          await mapTaskToBuildingBlock({
            buildingBlockId: blockId,
            taskId: taskId,
            orgId: String(userDetails.organisationId),
            createdBy: String(userDetails.userId),
          });
        }

        // 🔹 Remove unmapped blocks
        for (const blockId of blocksToRemove) {
          await removeSpecificTaskMapping(taskId, blockId);
        }

        // ✅ IMPORTANT FIX
        setExistingBlockIds([...selectedBlockIds]);
      } catch (err) {
        console.error("Failed updating building block mappings", err);
      }

      toast.success("Task updated successfully");
      onTaskUpdated(); // Refresh the task list
      onHide(); // Close modal
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // Function to get stage display name
  const getStageDisplayName = (stageId) => {
    if (!stageId) return "No Stage";
    const stage = stages.find((s) => s.stageId === stageId);
    return stage ? `${stage.stageName} (${stage.stageId})` : stageId;
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="fas fa-edit me-2"></i>
          Edit Task
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading task details...</span>
              </div>
              <p className="mt-2">Loading task details...</p>
            </div>
          ) : !taskData ? (
            <Alert variant="danger">Failed to load task details</Alert>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Task Name <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="taskName"
                      value={taskData.taskName || ""}
                      onChange={handleChange}
                      required
                      placeholder="Enter task name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned To</Form.Label>
                    {loadingTeam ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading team members..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        name="assignedTo"
                        value={taskData.assignedTo || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Team Member</option>
                        {teamMembers.map((memberId) => {
                          const displayName = getUserDisplayName(memberId);
                          return (
                            <option key={memberId} value={memberId}>
                              {displayName}
                            </option>
                          );
                        })}
                      </Form.Select>
                    )}
                    {teamMembers.length === 0 && !loadingTeam && (
                      <Form.Text
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        No team members available for this project
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Task Type <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskType"
                      value={taskData.taskType || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Task Type</option>
                      <option value="Development">Development</option>
                      <option value="Design">Design</option>
                      <option value="Testing">Testing</option>
                      <option value="Documentation">Documentation</option>
                      <option value="Research">Research</option>
                      <option value="Meeting">Meeting</option>
                      <option value="Maintenance">Maintenance</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Priority <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskPriority"
                      value={taskData.taskPriority || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Status <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskStatus"
                      value={taskData.taskStatus || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stage</Form.Label>
                    {loadingStages ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading stages..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        name="stageBelong"
                        value={taskData.stageBelong || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Stage (Optional)</option>
                        {/* <option value="">No Stage</option> */}
                        {stages.map((stage) => (
                          <option key={stage.stageId} value={stage.stageId}>
                            {stage.stageName} ({stage.stageId})
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    {stages.length === 0 && !loadingStages && (
                      <Form.Text
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        No stages available for this project
                      </Form.Text>
                    )}
                    {taskData.stageBelong && (
                      <Form.Text
                        className="text-info"
                        style={{ fontSize: "11px" }}
                      >
                        Current stage:{" "}
                        {getStageDisplayName(taskData.stageBelong)}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned Start Date <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="taskStartDate"
                      value={formatDateForInput(taskData.taskStartDate)}
                      onChange={handleDateChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned End Date <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="taskEndDate"
                      value={formatDateForInput(taskData.taskEndDate)}
                      onChange={handleDateChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Actual Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="actualStartDate"
                      value={formatDateForInput(taskData.actualStartDate)}
                      onChange={handleDateChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Actual End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="actualEndDate"
                      value={formatDateForInput(taskData.actualEndDate)}
                      onChange={handleDateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Building Block</Form.Label>

                    {loadingBlocks ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading building blocks..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        multiple
                        value={selectedBlockIds}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value,
                          );
                          setSelectedBlockIds(selected);
                        }}
                      >
                        <option value="">
                          Select Building Block (Optional)
                        </option>

                        {buildingBlocks.map((block) => (
                          <option
                            key={block.buildingBlockId}
                            value={block.buildingBlockId}
                          >
                            {block.buildingBlockName} ({block.buildingBlockId})
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Task Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="taskDescription"
                      value={taskData.taskDescription || ""}
                      onChange={handleChange}
                      placeholder="Enter detailed task description..."
                      maxLength={1000}
                    />
                    <Form.Text className="text-muted">
                      {taskData.taskDescription?.length || 0}/1000 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={saving || loading || !taskData}
          >
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TaskEditModal;

// import { useState, useEffect } from "react";
// import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { getTaskById, updateTask } from "../Api/TaskMaster";
// import { getTeamById } from "../Api/TeamMaster";
// import { getUsersByIds } from "../Api/Auth";
// import { fetchProjectDetails } from "../Api/ViewProjects";
// import { fetchStagesByProject } from "../Api/StageMaster";

// const TaskEditModal = ({ show, onHide, taskId, onTaskUpdated }) => {
//   const [loading, setLoading] = useState(false);
//   const [saving, setSaving] = useState(false);
//   const [taskData, setTaskData] = useState(null);
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [teamMemberDetails, setTeamMemberDetails] = useState([]);
//   const [loadingTeam, setLoadingTeam] = useState(false);
//   const [stages, setStages] = useState([]);
//   const [loadingStages, setLoadingStages] = useState(false);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const username = userDetails?.username;

//   // Fetch user details for team members
//   const fetchUserDetails = async (userIds) => {
//     if (!userIds || userIds.length === 0) {
//       setTeamMemberDetails([]);
//       return;
//     }
//     try {
//       const numericUserIds = userIds
//         .map((id) => parseInt(id))
//         .filter((id) => !isNaN(id));
//       if (numericUserIds.length === 0) {
//         setTeamMemberDetails([]);
//         return;
//       }
//       const response = await getUsersByIds(numericUserIds);
//       if (response && response.users) {
//         setTeamMemberDetails(response.users);
//       } else {
//         setTeamMemberDetails([]);
//       }
//     } catch (error) {
//       console.error("Error fetching user details:", error);
//       setTeamMemberDetails([]);
//     }
//   };

//   // Fetch team members when project is available
//   const fetchTeamMembers = async (projectId) => {
//     if (!projectId) return;
//     try {
//       setLoadingTeam(true);
//       // First get project details to get the team ID
//       const projectData = await fetchProjectDetails(projectId);
//       console.log("Fetched project data:", projectData);
//       if (projectData && projectData.teamAssigned) {
//         const teamData = await getTeamById(projectData.teamAssigned);
//         if (teamData && teamData.teamMembers) {
//           setTeamMembers(teamData.teamMembers);
//           await fetchUserDetails(teamData.teamMembers);
//         } else {
//           setTeamMembers([]);
//           setTeamMemberDetails([]);
//         }
//       } else {
//         setTeamMembers([]);
//         setTeamMemberDetails([]);
//       }
//     } catch (error) {
//       console.error("Error fetching team members:", error);
//       setTeamMembers([]);
//       setTeamMemberDetails([]);
//     } finally {
//       setLoadingTeam(false);
//     }
//   };

//   // Get user display name similar to TaskMaster
//   const getUserDisplayName = (memberId) => {
//     const userDetail = teamMemberDetails.find(
//       (user) => user.userId === parseInt(memberId)
//     );
//     if (userDetail) {
//       return `${userDetail.username} (${userDetail.email})`;
//     }
//     return memberId; // Fallback to ID if user details not found
//   };

//   const fetchProjectStages = async (projectId) => {
//     if (!projectId) return;
//     try {
//       setLoadingStages(true);
//       const stageData = await fetchStagesByProject(projectId);
//       console.log("Fetched stages for project:", stageData);
//       if (Array.isArray(stageData)) {
//         setStages(stageData);
//       } else {
//         setStages([]);
//       }
//     } catch (error) {
//       console.error("Error fetching stages:", error);
//       setStages([]);
//     } finally {
//       setLoadingStages(false);
//     }
//   };

//   // Fetch task details when modal opens
//   useEffect(() => {
//     if (show && taskId) {
//       loadTaskDetails();
//     }
//   }, [show, taskId]);

//   const loadTaskDetails = async () => {
//     setLoading(true);
//     try {
//       const taskDetails = await getTaskById(taskId);
//       setTaskData(taskDetails);

//       // Fetch team members for the project
//       if (taskDetails.projectBelong) {
//         await fetchTeamMembers(taskDetails.projectBelong);
//         await fetchProjectStages(taskDetails.projectBelong); // ⬅️ Add this line
//       }
//     } catch (error) {
//       console.error("Error loading task details:", error);
//       toast.error("Failed to load task details");
//       onHide();
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (e) => {
//     setTaskData({
//       ...taskData,
//       [e.target.name]: e.target.value,
//     });
//   };

//   const handleDateChange = (e) => {
//     setTaskData({
//       ...taskData,
//       [e.target.name]: e.target.value ? new Date(e.target.value) : null,
//     });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!taskData) return;

//     setSaving(true);
//     try {
//       const updateData = {
//         TaskName: taskData.taskName,
//         ProjectBelong: taskData.projectBelong,
//         AssignedTo: taskData.assignedTo,
//         TaskStartDate: taskData.taskStartDate,
//         TaskEndDate: taskData.taskEndDate,
//         TaskType: taskData.taskType,
//         TaskPriority: taskData.taskPriority,
//         TaskStatus: taskData.taskStatus,
//         TaskDescription: taskData.taskDescription,
//         ActualStartDate: taskData.actualStartDate,
//         ActualEndDate: taskData.actualEndDate,
//         EditedBy: username,
//       };

//       await updateTask(taskId, updateData);
//       toast.success("Task updated successfully");
//       onTaskUpdated(); // Refresh the task list
//       onHide(); // Close modal
//     } catch (error) {
//       console.error("Error updating task:", error);
//       toast.error("Failed to update task");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const formatDateForInput = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toISOString().split("T")[0];
//   };

//   if (!show) return null;

//   return (
//     <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
//       <Modal.Header closeButton className="bg-primary text-white">
//         <Modal.Title>
//           <i className="fas fa-edit me-2"></i>
//           Edit Task
//         </Modal.Title>
//       </Modal.Header>

//       <Form onSubmit={handleSubmit}>
//         <Modal.Body>
//           {loading ? (
//             <div className="text-center py-4">
//               <div className="spinner-border text-primary" role="status">
//                 <span className="visually-hidden">Loading task details...</span>
//               </div>
//               <p className="mt-2">Loading task details...</p>
//             </div>
//           ) : !taskData ? (
//             <Alert variant="danger">Failed to load task details</Alert>
//           ) : (
//             <>
//               <Row>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Task Name <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Control
//                       type="text"
//                       name="taskName"
//                       value={taskData.taskName || ""}
//                       onChange={handleChange}
//                       required
//                       placeholder="Enter task name"
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>Assigned To</Form.Label>
//                     {loadingTeam ? (
//                       <Form.Control
//                         type="text"
//                         disabled
//                         value="Loading team members..."
//                         className="bg-light"
//                       />
//                     ) : (
//                       <Form.Select
//                         name="assignedTo"
//                         value={taskData.assignedTo || ""}
//                         onChange={handleChange}
//                       >
//                         <option value="">Select Team Member</option>
//                         {teamMembers.map((memberId) => {
//                           const displayName = getUserDisplayName(memberId);
//                           return (
//                             <option key={memberId} value={memberId}>
//                               {displayName}
//                             </option>
//                           );
//                         })}
//                       </Form.Select>
//                     )}
//                     {teamMembers.length === 0 && !loadingTeam && (
//                       <Form.Text
//                         className="text-muted"
//                         style={{ fontSize: "11px" }}
//                       >
//                         No team members available for this project
//                       </Form.Text>
//                     )}
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Task Type <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Select
//                       name="taskType"
//                       value={taskData.taskType || ""}
//                       onChange={handleChange}
//                       required
//                     >
//                       <option value="">Select Task Type</option>
//                       <option value="Development">Development</option>
//                       <option value="Design">Design</option>
//                       <option value="Testing">Testing</option>
//                       <option value="Documentation">Documentation</option>
//                       <option value="Research">Research</option>
//                       <option value="Meeting">Meeting</option>
//                       <option value="Maintenance">Maintenance</option>
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Priority <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Select
//                       name="taskPriority"
//                       value={taskData.taskPriority || ""}
//                       onChange={handleChange}
//                       required
//                     >
//                       <option value="">Select Priority</option>
//                       <option value="Low">Low</option>
//                       <option value="Medium">Medium</option>
//                       <option value="High">High</option>
//                       <option value="Critical">Critical</option>
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Status <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Select
//                       name="taskStatus"
//                       value={taskData.taskStatus || ""}
//                       onChange={handleChange}
//                       required
//                     >
//                       <option value="">Select Status</option>
//                       <option value="Not Started">Not Started</option>
//                       <option value="In Progress">In Progress</option>
//                       <option value="On Hold">On Hold</option>
//                       <option value="Completed">Completed</option>
//                       <option value="Cancelled">Cancelled</option>
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Planned Start Date <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="taskStartDate"
//                       value={formatDateForInput(taskData.taskStartDate)}
//                       onChange={handleDateChange}
//                       required
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>
//                       Planned End Date <span className="text-red">**</span>
//                     </Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="taskEndDate"
//                       value={formatDateForInput(taskData.taskEndDate)}
//                       onChange={handleDateChange}
//                       required
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>Actual Start Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="actualStartDate"
//                       value={formatDateForInput(taskData.actualStartDate)}
//                       onChange={handleDateChange}
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col md={6}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>Actual End Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="actualEndDate"
//                       value={formatDateForInput(taskData.actualEndDate)}
//                       onChange={handleDateChange}
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={12}>
//                   <Form.Group className="mb-3">
//                     <Form.Label>Task Description</Form.Label>
//                     <Form.Control
//                       as="textarea"
//                       rows={4}
//                       name="taskDescription"
//                       value={taskData.taskDescription || ""}
//                       onChange={handleChange}
//                       placeholder="Enter detailed task description..."
//                       maxLength={1000}
//                     />
//                     <Form.Text className="text-muted">
//                       {taskData.taskDescription?.length || 0}/1000 characters
//                     </Form.Text>
//                   </Form.Group>
//                 </Col>
//               </Row>
//             </>
//           )}
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={onHide} disabled={saving}>
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             type="submit"
//             disabled={saving || loading || !taskData}
//           >
//             {saving ? (
//               <>
//                 <span
//                   className="spinner-border spinner-border-sm me-2"
//                   role="status"
//                   aria-hidden="true"
//                 ></span>
//                 Saving...
//               </>
//             ) : (
//               "Save Changes"
//             )}
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default TaskEditModal;
